import {Box} from '@mantine/core';
import React, {useState} from 'react';

import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import CreateProteinForm, {
  getCreateFormValidation,
  isSelectedTranslationLink,
  TranslationLinkFields,
  TranslationLinkOrDummy,
  UNSELECTED_DUMMY_OBJECT,
} from 'molbio/aa-sequences/CreateAaSequenceForm';
import {BioentityLinkFieldWithTargetSchema} from 'schemas/fields/SchemaFieldConstants';
import useSchemaSSIsByIdQuery from 'schemas/util/useSchemaSSIsByIdQuery';
import {StudySearchItem} from 'studies/StudySearchInput';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {Optional} from 'util/Types';

export interface ConfirmCreateProteinPayload {
  name: string;
  folderId: string;
  translationLink?: BioentityLinkFieldWithTargetSchema | null;
  tagSchemaId?: string;
  studyIds?: Array<string>;
}
export interface CreateProteinModalProps {
  title: string;
  description?: string;
  initialFolderId?: string;
  namePlaceholder?: string;
  onRequestSubmit: (result: ConfirmCreateProteinPayload) => void;
  onRequestClose: () => void;
  translationLinkFields?: TranslationLinkFields;
  oligoAwareFolderItemTypes: Array<OligoAwareFolderItemType>;
  linkFieldLabel: string;
}

export default function CreateProteinModal({
  title,
  description,
  initialFolderId,
  namePlaceholder,
  onRequestSubmit,
  onRequestClose,
  translationLinkFields = [],
  oligoAwareFolderItemTypes,
  linkFieldLabel,
}: CreateProteinModalProps): JSX.Element {
  const [name, setName] = useState(namePlaceholder ?? '');
  const [folderId, setFolderId] = useState<Optional<string>>(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [tagSchemaId, setTagSchemaId] = useState<string | null>(null);
  const [translationLink, setTranslationLink] = useState<TranslationLinkOrDummy>(UNSELECTED_DUMMY_OBJECT);
  const [selectedStudyItems, setSelectedStudyItems] = useState<Array<StudySearchItem>>([]);

  const {interfaceIdsBySchemaId} = useSchemaSSIsByIdQuery();

  const {isValid} = getCreateFormValidation({
    name,
    folderId,
    translationLink,
    translationLinkFields,
    tagSchemaId,
    interfaceIdsBySchemaId,
  });

  const handleConfirm = (): void => {
    const translationLinkValue: BioentityLinkFieldWithTargetSchema | null = isSelectedTranslationLink(
      translationLink
    )
      ? translationLink
      : null;
    const confirmPayload: ConfirmCreateProteinPayload = {
      folderId: folderId!,
      name,
      translationLink: translationLinkValue,
    };
    if (tagSchemaId != null) {
      confirmPayload.tagSchemaId = tagSchemaId;
    }
    if (selectedStudyItems.length > 0) {
      confirmPayload.studyIds = selectedStudyItems.map((studyItem) => studyItem.id);
    }
    onRequestSubmit(confirmPayload);
    onRequestClose();
  };

  const handleFolderIdChange = (newFolderId: string): void => {
    setFolderId(newFolderId);
    FolderDefaults.set(FolderDefaultsKey.INVENTORY, newFolderId);
  };

  const buttons = [
    {
      text: 'Cancel',
      cancel: true,
    },
    {
      text: 'Create',
      submit: true,
      style: ButtonStyle.PRIMARY,
      disabled: !isValid,
      onClick: handleConfirm,
    },
  ];

  return (
    <Modal
      title={title}
      showHeaderBorder={false}
      enableRedesign={true}
      onRequestClose={onRequestClose}
      buttons={buttons}
    >
      {description != null && <Box pb="sm">{description}</Box>}
      <CreateProteinForm
        name={name}
        folderId={folderId}
        tagSchemaId={tagSchemaId}
        translationLink={translationLink}
        selectedStudyItems={selectedStudyItems}
        onNameChange={setName}
        onFolderIdChange={handleFolderIdChange}
        onTagSchemaIdChange={setTagSchemaId}
        onTranslationLinkChange={setTranslationLink}
        onSelectedStudyItemsChange={setSelectedStudyItems}
        translationLinkFields={translationLinkFields}
        oligoAwareFolderItemTypes={oligoAwareFolderItemTypes}
        linkFieldLabel={linkFieldLabel}
      />
    </Modal>
  );
}
